import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../eco-icon/EcoIcon";
import type { StoreForEvaluation } from "./SelectFreelancersForEvaluationModal";
import type { StoreEvaluation } from "../../../views/store-evaluations/storeEvaluationsApi";
import { SUPERVISOR_STORE_EVALUATION_PAGE } from "../../../views/supervisor/routes";

import "./EvalSheet.css";

type FetchStoresPageResult = { items: StoreForEvaluation[]; total: number };

type Props = {
  isOpen: boolean;
  onClose: () => void;
  token: string;

  fetchStoresPage: (args: { token: string; page: number; limit: number }) => Promise<FetchStoresPageResult>;

  fetchStoreEvaluations: (args: {
    token: string;
    storeId: string;
    referenceDate: string;
    includeAnswers: boolean;
  }) => Promise<StoreEvaluation[]>;
};

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfWeekMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

const weekTitle = (start: Date) => {
  const end = addDays(start, 6);
  return start.getMonth() === end.getMonth()
    ? `${start.getDate()} a ${end.getDate()} de ${MONTHS[end.getMonth()]}`
    : `${start.getDate()} de ${MONTHS[start.getMonth()]} a ${end.getDate()} de ${MONTHS[end.getMonth()]}`;
};

const time = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";

/** Faixas de nota — as mesmas da lista e do painel de Qualidade. */
const scoreBand = (score01: number) => (score01 >= 0.8 ? "good" : score01 >= 0.5 ? "warn" : "bad");

const STORES_LIMIT = 10;
type Step = "STORE" | "DAY";

/**
 * Avaliações de loja enviadas (redesign de 15/10/2026): escolhida a loja, o dia
 * mostra as avaliações na hora; cada uma abre o detalhe (respostas e fotos).
 */
const ViewStoreEvaluationsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  token,
  fetchStoresPage,
  fetchStoreEvaluations,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("STORE");

  const [storesPage, setStoresPage] = useState(1);
  const [storesTotal, setStoresTotal] = useState(0);
  const [stores, setStores] = useState<StoreForEvaluation[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [selectedStore, setSelectedStore] = useState<StoreForEvaluation | null>(null);

  const [weekAnchor, setWeekAnchor] = useState<Date>(() => new Date());
  const weekStart = useMemo(() => startOfWeekMonday(weekAnchor), [weekAnchor]);
  const daysOfWeek = useMemo(() => {
    const labels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
    return labels.map((label, idx) => {
      const d = addDays(weekStart, idx);
      return { label, date: d, ymd: toYmd(d) };
    });
  }, [weekStart]);
  const [selectedDayYmd, setSelectedDayYmd] = useState<string>(() => toYmd(new Date()));

  const [loadingEvaluations, setLoadingEvaluations] = useState(false);
  const [evaluations, setEvaluations] = useState<StoreEvaluation[]>([]);

  const fetchStores = useCallback(
    async (page: number) => {
      if (!token) return;
      try {
        setLoadingStores(true);
        const data = await fetchStoresPage({ token, page, limit: STORES_LIMIT });
        const items = data.items ?? [];
        setStores((prev) => (page === 1 ? items : [...prev, ...items]));
        setStoresTotal(data.total ?? 0);
      } catch (err: any) {
        toast.error(err?.message ?? "Erro ao buscar lojas.");
      } finally {
        setLoadingStores(false);
      }
    },
    [token, fetchStoresPage],
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    if (!token) {
      toast.error("Sessão inválida. Faça login novamente.");
      onClose();
      return;
    }
    setStep("STORE");
    setStoresPage(1);
    setStoresTotal(0);
    setStores([]);
    setSelectedStore(null);
    setWeekAnchor(new Date());
    setSelectedDayYmd(toYmd(new Date()));
    setEvaluations([]);
  }, [isOpen, token, onClose]);

  useEffect(() => {
    if (!isOpen || step !== "STORE") return;
    fetchStores(storesPage);
  }, [isOpen, step, storesPage, fetchStores]);

  // Loja e dia escolhidos: carrega as avaliações do dia.
  useEffect(() => {
    if (!isOpen || step !== "DAY" || !selectedStore?.id || !token) return;
    let cancelled = false;
    setLoadingEvaluations(true);
    setEvaluations([]);
    fetchStoreEvaluations({ token, storeId: selectedStore.id, referenceDate: selectedDayYmd, includeAnswers: false })
      .then((list) => {
        if (!cancelled) setEvaluations(list ?? []);
      })
      .catch((err: any) => {
        if (!cancelled) toast.error(err?.message ?? "Erro ao listar avaliações da loja.");
      })
      .finally(() => {
        if (!cancelled) setLoadingEvaluations(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, step, selectedStore?.id, selectedDayYmd, token, fetchStoreEvaluations]);

  const handleSelectStore = (store: StoreForEvaluation) => {
    setSelectedStore(store);
    setWeekAnchor(new Date());
    setSelectedDayYmd(toYmd(new Date()));
    setStep("DAY");
  };

  const changeWeek = (days: number) => {
    const next = addDays(weekAnchor, days);
    setWeekAnchor(next);
    setSelectedDayYmd(toYmd(startOfWeekMonday(next)));
  };

  const openEvaluation = (id: string) => {
    onClose();
    navigate(SUPERVISOR_STORE_EVALUATION_PAGE(id));
  };

  if (!isOpen) return null;

  const remainingStores = Math.max(0, storesTotal - stores.length);
  const [, mm, dd] = selectedDayYmd.split("-");

  return (
    <div className="evalSheet__overlay" onClick={onClose} role="presentation">
      <div
        className="evalSheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="viewStoreEvals-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="evalSheet__grabber" aria-hidden="true">
          <span />
        </div>
        <header className="evalSheet__header">
          <div className="evalSheet__titles">
            <h2 id="viewStoreEvals-title" className="evalSheet__title">
              Avaliações de loja
            </h2>
          </div>
          <button type="button" className="evalSheet__close" onClick={onClose} aria-label="Fechar" data-modal-close>
            <EcoIcon name="x" size={20} />
          </button>
        </header>

        <div className="evalSheet__body">
          {step === "STORE" ? (
            <section className="evalSheet__group" aria-label="Lojas">
              <span className="evalSheet__label">Escolha a loja</span>
              {loadingStores && stores.length === 0 ? (
                <div className="evalSheet__empty">Carregando lojas...</div>
              ) : stores.length === 0 ? (
                <div className="evalSheet__empty">Nenhuma loja encontrada.</div>
              ) : (
                stores.map((store) => (
                  <button key={store.id} type="button" className="evalSheet__item" onClick={() => handleSelectStore(store)}>
                    <span className="evalSheet__itemIcon" aria-hidden="true">
                      <EcoIcon name="store" size={20} />
                    </span>
                    <span className="evalSheet__optionText">
                      <span className="evalSheet__optionTitle">{store.nomeFantasia}</span>
                      <span className="evalSheet__optionSub">
                        {store.endereco || "Endereço não informado"}
                        {store.estado ? ` · ${store.estado}` : ""}
                      </span>
                    </span>
                    <EcoIcon name="chevronRight" size={18} className="evalSheet__chevron" />
                  </button>
                ))
              )}
              {remainingStores > 0 ? (
                <button
                  type="button"
                  className="evalSheet__more"
                  onClick={() => setStoresPage((p) => p + 1)}
                  disabled={loadingStores}
                >
                  {loadingStores ? "Carregando..." : `Mostrar mais ${remainingStores} ${remainingStores === 1 ? "loja" : "lojas"}`}
                </button>
              ) : null}
            </section>
          ) : (
            <>
              <div className="evalSheet__chosen">
                <span className="evalSheet__optionText">
                  <span className="evalSheet__optionSub">Loja</span>
                  <span className="evalSheet__optionTitle">{selectedStore?.nomeFantasia ?? "—"}</span>
                </span>
                <button type="button" className="evalSheet__link" onClick={() => setStep("STORE")}>
                  Trocar
                </button>
              </div>

              <div className="evalSheet__group">
                <div className="evalSheet__week">
                  <button type="button" className="evalSheet__iconBtn" aria-label="Semana anterior" onClick={() => changeWeek(-7)}>
                    <EcoIcon name="chevronLeft" size={18} />
                  </button>
                  <span className="evalSheet__weekTitle">{weekTitle(weekStart)}</span>
                  <button type="button" className="evalSheet__iconBtn" aria-label="Próxima semana" onClick={() => changeWeek(7)}>
                    <EcoIcon name="chevronRight" size={18} />
                  </button>
                </div>
                <div className="evalSheet__days" role="group" aria-label="Dia">
                  {daysOfWeek.map((d) => (
                    <button
                      key={d.ymd}
                      type="button"
                      className="evalSheet__day"
                      aria-pressed={d.ymd === selectedDayYmd}
                      onClick={() => setSelectedDayYmd(d.ymd)}
                    >
                      <span className="evalSheet__dayName">{d.label}</span>
                      <span className="evalSheet__dayNum">{d.date.getDate()}</span>
                    </button>
                  ))}
                </div>
              </div>

              <section className="evalSheet__group" aria-live="polite" aria-label="Avaliações do dia">
                <span className="evalSheet__label">
                  {loadingEvaluations
                    ? `Carregando ${dd}/${mm}...`
                    : `${evaluations.length} ${evaluations.length === 1 ? "avaliação" : "avaliações"} em ${dd}/${mm}`}
                </span>
                {!loadingEvaluations && evaluations.length === 0 ? (
                  <div className="evalSheet__empty">Nenhuma avaliação encontrada para esta data.</div>
                ) : null}
                {evaluations.map((ev) => {
                  const hasScore = ev.score01 !== null && ev.score01 !== undefined;
                  const score = hasScore ? Number(ev.score01) : 0;
                  const submitted = ev.status === "SUBMITTED";
                  return (
                    <button key={ev.id} type="button" className="evalSheet__evalCard" onClick={() => openEvaluation(ev.id)}>
                      <span className="evalSheet__evalTop">
                        <span className="evalSheet__optionText">
                          <span className="evalSheet__itemTitle">{ev.form?.title || ev.formType?.name || "Formulário"}</span>
                          <span className="evalSheet__optionSub">
                            {time(ev.answeredAt)}
                            {ev.answeredBy?.nomeCompleto ? ` · ${ev.answeredBy.nomeCompleto}` : ""}
                          </span>
                        </span>
                        <span className={`evalSheet__pill evalSheet__pill--${submitted ? "good" : "warn"}`}>
                          {submitted ? "Finalizada" : "Aberta"}
                        </span>
                      </span>
                      {hasScore ? (
                        <span className="evalSheet__score">
                          <span className="evalSheet__bar" aria-hidden="true">
                            <span
                              className={`evalSheet__barFill evalSheet__barFill--${scoreBand(score)}`}
                              style={{ width: `${Math.max(0, Math.min(1, score)) * 100}%` }}
                            />
                          </span>
                          <strong>{(score * 100).toFixed(0)}%</strong>
                          {ev.stars !== null && ev.stars !== undefined ? (
                            <span className="evalSheet__optionSub">
                              {Number(ev.stars).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} de 5
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="evalSheet__optionSub">Sem nota até finalizar</span>
                      )}
                      <span className="evalSheet__seeMore">
                        Ver respostas e fotos
                        <EcoIcon name="chevronRight" size={16} />
                      </span>
                    </button>
                  );
                })}
              </section>
            </>
          )}
        </div>

        <footer className="evalSheet__footer">
          <button type="button" className="ecoBtn" onClick={onClose}>
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ViewStoreEvaluationsModal;
